import { Formik } from "formik";
import { Form } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import * as yup from "yup";

import { Loader } from "../../../common/components/core";
import {
    FormContainer,
    FormFooter,
    InputField,
    SelectField,
} from "../../../common/components/form";
import { countryCityOptions } from "../../../constants/locals";
import { FormSubmissionHandler } from "../../../types/common";
import useProfileTypeOptions from "../hooks/useProfileTypeOptions";

export type CreateNewProfileFormValues = {
    entityType: string;
    entityName: string;
    entityCountryCity: string;
    contactPersonName: string;
    contactPersonNumber: string;
    contactPersonEmail: string;
};

const schema: yup.SchemaOf<CreateNewProfileFormValues> = yup.object().shape({
    entityType: yup.string().required(),
    entityName: yup.string().required(),
    entityCountryCity: yup.string().required(),
    contactPersonName: yup.string().required(),
    contactPersonNumber: yup.string().required(),
    contactPersonEmail: yup
        .string()
        .email("Invalid email address format.")
        .required(),
});

type CreateNewProfileFormProps = {
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<CreateNewProfileFormValues>;
};

const CreateNewProfileForm = ({
    onCancel,
    onSubmit,
}: CreateNewProfileFormProps): JSX.Element => {
    const { profileTypeOptions } = useProfileTypeOptions();

    return (
        <Formik
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={onSubmit}
            initialValues={{
                entityType: "",
                entityName: "",
                entityCountryCity: "",
                contactPersonName: "",
                contactPersonNumber: "",
                contactPersonEmail: "",
            }}
        >
            {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
                <Loader isLoading={isSubmitting}>
                    <FormContainer>
                        <h1 className="mb-3 rem-1-5">Enter Details for New Profile</h1>
                        <Form
                            autoComplete="off"
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <SelectField
                                name="entityType"
                                label="Profile Type"
                                options={profileTypeOptions}
                                required={true}
                            />
                            <InputField
                                name="entityName"
                                label="Profile Name"
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue(
                                        "entityName",
                                        values.entityName.trim(),
                                    )
                                }
                                required={true}
                            />
                            <SelectField
                                name="entityCountryCity"
                                label="Profile Country & City"
                                options={countryCityOptions}
                                required={true}
                            />
                            <div>
                                <hr className="my-3" />
                                <h1 className="mb-3 rem-1-5">Enter Details for Profile Contact Person </h1>
                            </div>
                            <InputField
                                name="contactPersonName"
                                label="Contact Person Name"
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue(
                                        "contactPersonName",
                                        values.contactPersonName.trim(),
                                    )
                                }
                                required={true}
                            />
                            <InputField
                                name="contactPersonNumber"
                                label="Contact Person Number"
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue(
                                        "contactPersonName",
                                        values.contactPersonName.trim(),
                                    )
                                }
                                required={true}
                            />
                            <InputField
                                name="contactPersonEmail"
                                label="Contact Person Email address"
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue(
                                        "contactPersonEmail",
                                        values.contactPersonEmail.trim(),
                                    )
                                }
                                required={true}
                            />
                            <FormFooter className="d-flex justify-content-between">
                                <Button
                                    variant="light.tertiary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <Button variant="light.primary" type="submit">
                                    Create
                                </Button>
                            </FormFooter>
                        </Form>
                    </FormContainer>
                </Loader>
            )}
        </Formik>
    );
};

export default CreateNewProfileForm;
